/**
 * EventsTab — Feb 2026
 *
 * Bulletin Board → Events sub-tab. Celebration-first gallery of HR albums.
 * Pinned album lives at top with a bigger banner card; the rest render as
 * a masonry-ish grid. New-in-7-days albums get a pink "New" pill.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, Camera } from "lucide-react";
import EventAlbumCard from "./EventAlbumCard";
import EventAlbumDialog from "./EventAlbumDialog";
import EventAlbumView from "./EventAlbumView";

export default function EventsTab({ wsId }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ can_post: false, is_moderator: false });
  const [busy, setBusy] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | album object for edit
  const [openId, setOpenId] = useSearchParamsState("album");
  const [refreshTick, setRefreshTick] = useState(0);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/event-albums?published_only=false&limit=100`);
      setRows(data?.rows || []);
      setMeta({ can_post: !!data?.can_post, is_moderator: !!data?.is_moderator });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load events")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId, refreshTick]);

  const pinned = useMemo(() => rows.find((r) => r.pinned && r.status === "published"), [rows]);
  const others = useMemo(() => rows.filter((r) => r !== pinned), [rows, pinned]);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="events-tab">
      {/* Hero strip — celebration tone */}
      <div className="relative overflow-hidden bg-gradient-to-r from-fuchsia-500 via-pink-500 to-orange-500 text-white">
        <div className="pointer-events-none absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative px-4 sm:px-6 py-5 sm:py-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-white/80 text-[11px] tracking-widest uppercase">
              <Sparkles className="w-3.5 h-3.5" /> HR-published moments
            </div>
            <div className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>
              Our story, in pictures.
            </div>
            <div className="text-sm text-white/90 mt-1 max-w-xl">
              Every off-site, cake-cutting, town-hall and Diwali party — all in one place. Tag your team, drop a 🎉, relive the day.
            </div>
          </div>
          {meta.can_post && (
            <Button
              onClick={() => setEditing({})}
              className="rounded-none bg-white text-fuchsia-700 hover:bg-white/90 shrink-0"
              data-testid="events-new-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New event album
            </Button>
          )}
        </div>
      </div>

      {busy ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No albums yet"
          description={meta.can_post
            ? "Kick off with your first event album — upload photos and videos from the last team activity."
            : "Once HR uploads photos from an event, you'll see them here."}
          icon={Camera}
          testid="events-empty"
        />
      ) : (
        <div className="space-y-4">
          {pinned && (
            <div data-testid={`events-pinned-${pinned.id}`}>
              <EventAlbumCard
                album={pinned}
                featured
                onOpen={() => setOpenId(pinned.id)}
                onEdit={() => setEditing(pinned)}
                canModerate={meta.is_moderator}
                onChanged={() => setRefreshTick((n) => n + 1)}
              />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4" data-testid="events-grid">
            {others.map((a) => (
              <EventAlbumCard
                key={a.id}
                album={a}
                onOpen={() => setOpenId(a.id)}
                onEdit={() => setEditing(a)}
                canModerate={meta.is_moderator}
                onChanged={() => setRefreshTick((n) => n + 1)}
              />
            ))}
          </div>
        </div>
      )}

      {editing !== null && (
        <EventAlbumDialog
          wsId={wsId}
          album={editing?.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={(alb) => { setEditing(null); setRefreshTick((n) => n + 1); if (alb?.id) setOpenId(alb.id); }}
        />
      )}
      {openId && (
        <EventAlbumView
          albumId={openId}
          onClose={() => setOpenId(null)}
          onChanged={() => setRefreshTick((n) => n + 1)}
        />
      )}
    </div>
  );
}

// Tiny helper — mirror `?album=<id>` in the URL so tagged-in-photo notifications
// deep-link straight into the right album. Also survives page reloads.
function useSearchParamsState(key) {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) || null;
  const set = (v) => {
    const s = new URLSearchParams(params);
    if (v) s.set(key, v); else s.delete(key);
    setParams(s, { replace: true });
  };
  return [value, set];
}
